// apps/dashboard/src/lib/device-heartbeat.ts
//
// Background device-presence beat.
//
// `fetch` does NOT throw on a 4xx — it resolves with `ok: false` — so the previous
// `try { await fetch(...) } catch { /* silent */ }` loop could never notice a rejected
// session. A session the server had already rejected therefore kept beating every 120s
// until the tab closed: 31 of 1,746 heartbeats in 24h (1.8%) were 401s from
// `requireAuth` (apps/api/src/modules/device/presentation/device.routes.ts). Nothing
// crashed, nothing was logged, and it never self-healed — `getIdToken()` serves a CACHED
// token while it believes that token is still valid, so a revoked session or client clock
// skew replayed the same doomed credential indefinitely.
//
// Contract: silent by design. This is background maintenance, never user-facing — no
// toasts, no console noise, no Sentry events. Exactly ONE forced token refresh + retry per
// beat on a 401 (never a storm); a second 401 means the session is genuinely gone, so the
// loop stops rather than beating at a wall. Transient failures (network throw, 5xx) keep
// the loop alive.
//
// Kept byte-identical to the dashboard copy (apart from this path comment) so the two can
// be lifted into a shared package in one move — same convention as ./device.ts.

const HEARTBEAT_INTERVAL_MS = 120_000;

export interface DeviceHeartbeatOptions {
  /** Non-empty device id; sent both as the `X-Device-Id` header and in the body. */
  deviceId: string;
  /** Resolves the bearer token. `forceRefresh` MUST bypass the Firebase token cache. */
  getToken: (forceRefresh: boolean) => Promise<string>;
  /** Endpoint to POST to, e.g. `/api/proxy/devices/heartbeat`. */
  endpoint: string;
  /** Overridable for tests. */
  intervalMs?: number;
  /** Overridable for tests. */
  fetchImpl?: typeof fetch;
}

/**
 * Starts the heartbeat. Returns an idempotent stop function; the loop may also stop
 * itself once the server has conclusively rejected the session (see the 401 handling).
 */
export function startDeviceHeartbeat({
  deviceId,
  getToken,
  endpoint,
  intervalMs = HEARTBEAT_INTERVAL_MS,
  fetchImpl,
}: DeviceHeartbeatOptions): () => void {
  const doFetch = fetchImpl ?? ((...args: Parameters<typeof fetch>) => fetch(...args));

  let timer: ReturnType<typeof setInterval> | null = null;
  let inFlight = false;

  const stop = (): void => {
    if (timer === null) return;
    clearInterval(timer);
    timer = null;
  };

  const post = async (forceRefresh: boolean): Promise<Response> => {
    const token = await getToken(forceRefresh);
    return doFetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Device-Id': deviceId,
      },
      body: JSON.stringify({ deviceId }),
    });
  };

  const beat = async (): Promise<void> => {
    // A hung request must not get another beat stacked on top of it 120s later.
    if (inFlight) return;
    inFlight = true;
    try {
      const res = await post(false);
      // Anything that is not a 401 is either fine or transient (5xx, proxy hiccup).
      // Only an auth rejection is worth acting on; the controller returns 204 even for
      // internal failures precisely so clients don't retry hard.
      if (res.status !== 401) return;

      // The token we sent was rejected. It may just be a stale cached one, so force a
      // refresh and retry exactly once.
      const retried = await post(true);
      if (retried.status === 401) {
        // A freshly minted token was rejected too: the session is genuinely gone
        // (revoked, user disabled, account deleted). Beating for the rest of the tab's
        // life would change nothing. A later sign-in restarts this via onAuthStateChanged.
        stop();
      }
    } catch {
      // Network-level failure (offline, proxy restarting) or a token refresh that could
      // not complete. Transient by nature — stay silent and beat again next tick.
    } finally {
      inFlight = false;
    }
  };

  timer = setInterval(beat, intervalMs);
  return stop;
}
